/**
 * KYC-design (2026-09-08) — the model helpers behind the redesigned client form: fixed
 * progress SECTIONS, row-card TEMPLATES, ADDRESS values and DISPLAY formatting for the
 * review page. Pure functions over the declaration and the answers; no DOM.
 */
import type {
  AddressValue,
  FieldSpec,
  FieldValue,
  FormSchema,
  FormStep,
  FormValues,
  GroupFieldSpec,
  GroupRow,
  ScalarFieldSpec,
} from "../types.js";
import { ADDRESS_KEYS } from "../types.js";
import type { PredicateScope } from "./predicates.js";
import { evaluate, resolvePath } from "./predicates.js";
import { GROUP_LIST_SEPARATOR, asStringArray, fromRowCell, humanise } from "./formModel.js";

/* ── Progress sections ─────────────────────────────────────────────────────────────── */

export interface ProgressSection {
  /** The section's stable key: the first step's `section` or its id. */
  key: string;
  title: string;
  note?: string;
  /** Step ids that belong to this section, in declared order. */
  stepIds: string[];
  /** A section whose every step is gated on `fields.` predicates that currently fail. */
  skipped: boolean;
}

/**
 * The FIXED progress list. A step hidden by a `ctx.`-only predicate (a service flag decided
 * before the form was sent) is not in the journey at all and is left out. A step whose
 * predicate depends on `fields.` answers STAYS in the list — it may be skipped on the way
 * through, but it never renumbers the sections the client was shown on the first page.
 * Consecutive steps sharing a `section` collapse into one entry.
 */
export function progressSections(schema: FormSchema, scope: PredicateScope): ProgressSection[] {
  const sections: ProgressSection[] = [];
  for (const step of schema.steps ?? []) {
    if (step.visible_when && !evaluateCtxOnly(step, scope)) continue;
    const key = step.section ?? step.id;
    const title = step.section ?? step.title ?? humanise(step.id);
    const skipped = step.visible_when !== undefined && dependsOnFields(step) && !evaluate(step.visible_when, scope);
    const last = sections[sections.length - 1];
    if (last && last.key === key) {
      last.stepIds.push(step.id);
      last.skipped = last.skipped && skipped;
      if (!last.note && step.note) last.note = step.note;
      continue;
    }
    sections.push({ key, title, note: step.note, stepIds: [step.id], skipped });
  }
  return sections;
}

/** Evaluate a step's predicate treating every `fields.` leaf as TRUE (unknown until answered). */
function evaluateCtxOnly(step: FormStep, scope: PredicateScope): boolean {
  if (!step.visible_when) return true;
  return evaluateWithFieldsAs(step.visible_when, scope, true);
}

function dependsOnFields(step: FormStep): boolean {
  const walk = (node: unknown): boolean => {
    if (!node || typeof node !== "object") return false;
    const n = node as Record<string, unknown>;
    if (typeof n.path === "string") return n.path.startsWith("fields.");
    if (Array.isArray(n.all)) return n.all.some(walk);
    if (Array.isArray(n.any)) return n.any.some(walk);
    return false;
  };
  return walk(step.visible_when);
}

function evaluateWithFieldsAs(node: unknown, scope: PredicateScope, fieldsAs: boolean): boolean {
  if (!node || typeof node !== "object") return true;
  const n = node as Record<string, unknown>;
  if (Array.isArray(n.all)) return n.all.every((c) => evaluateWithFieldsAs(c, scope, fieldsAs));
  if (Array.isArray(n.any)) return n.any.some((c) => evaluateWithFieldsAs(c, scope, fieldsAs));
  if (typeof n.path === "string" && n.path.startsWith("fields.")) return fieldsAs;
  return evaluate(node as never, scope);
}

/** Which section (index) a visible step belongs to; -1 when it is not in the list. */
export function sectionIndexOf(sections: readonly ProgressSection[], stepId: string): number {
  return sections.findIndex((s) => s.stepIds.includes(stepId));
}

/* ── Row templates ─────────────────────────────────────────────────────────────────── */

/**
 * "{{fields.full_name}}" style templates over a group row. Unknown or empty references
 * resolve to "" and the surrounding separators are tidied, so "{{fields.role}} · {{fields.email}}"
 * with no email reads "Director", not "Director · ".
 *
 * A cell holding a LIST (a `checkbox_group` subfield) resolves EACH selection to its own
 * label and rejoins them on the separator the cell was written with — looking the whole joined
 * cell up as one option value would match nothing and print the raw wire tokens.
 *
 * A cell holding an OBJECT (an `address` subfield — the one non-string cell a row can hold,
 * Phase 541) renders as the one-line address {@link formatAddress} makes of it, so
 * `item_title: "{{fields.<address subfield>}}"` prints the address rather than nothing.
 */
export function renderRowTemplate(template: string, row: GroupRow, group: GroupFieldSpec): string {
  const rendered = template.replace(/\{\{\s*fields\.([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g, (_m, name: string) => {
    const sub = group.fields.find((f) => f.name === name);
    const raw = row[name] ?? "";
    if (typeof raw !== "string") return cellLine(raw);
    if (!sub) return raw;
    const cell = fromRowCell(sub.type, raw);
    if (Array.isArray(cell)) {
      return asStringArray(cell).map((value) => optionLabelFor(sub, value)).join(GROUP_LIST_SEPARATOR);
    }
    return optionLabelFor(sub, raw);
  });
  return rendered
    .split(/\s*·\s*/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .join(" · ")
    .trim();
}

function optionLabelFor(field: ScalarFieldSpec, value: string): string {
  if (!field.options) return value;
  for (const opt of field.options) {
    if (typeof opt === "string") { if (opt === value) return humanise(opt); continue; }
    if (opt.value === value) return opt.label?.trim() || humanise(opt.value);
  }
  return value;
}

/**
 * ONE row cell as ONE line of display copy — the rule every surface that prints a raw cell
 * shares, so a row's non-string cell cannot read as `[object Object]` on one of them and as an
 * address on another.
 *
 * A string cell is its own line, VERBATIM (untrimmed — the caller decides what an all-space
 * cell means); the one non-string cell a row can hold is an `address`, and it reads as the
 * same one-line address {@link formatAddress} gives it everywhere else.
 */
export function cellLine(cell: string | AddressValue | undefined): string {
  if (cell === undefined) return "";
  if (typeof cell === "string") return cell;
  return formatAddress(asAddress(cell));
}

export function rowTitle(group: GroupFieldSpec, row: GroupRow, index: number, label: string): string {
  if (group.item_title) {
    const t = renderRowTemplate(group.item_title, row, group);
    if (t.length > 0) return t;
  }
  return `${label} ${index + 1}`;
}

export function rowSummary(group: GroupFieldSpec, row: GroupRow): string {
  return group.item_summary ? renderRowTemplate(group.item_summary, row, group) : "";
}

/* ── Addresses ─────────────────────────────────────────────────────────────────────── */

/**
 * Card #1998 — the premise coordinates the postcode finder publishes with a PICKED address. They
 * ride the address into the submission (so the engine can measure it against the tenant's office
 * for a `from_ctx.distance` pre-answer). Only a FINITE latitude/longitude PAIR counts; half a pair
 * or a non-number is no coordinate at all. Never shown, never asked for. The one place they are
 * dropped is a hand edit of the POSTCODE in `AddressInput` (BB walk 2: the distance is
 * postcode-level, so correcting a line on the same postcode keeps them); every other reader —
 * the autosave restore, the `same_as` copy — keeps them.
 */
export type AddressGeo = { latitude?: number; longitude?: number };

export function addressGeo(item: unknown): AddressGeo {
  const source = (typeof item === "object" && item !== null ? item : {}) as Record<string, unknown>;
  const { latitude, longitude } = source;
  return typeof latitude === "number" && Number.isFinite(latitude) && typeof longitude === "number" && Number.isFinite(longitude)
    ? { latitude, longitude }
    : {};
}

export function asAddress(value: FieldValue | undefined): AddressValue {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const out: AddressValue = {};
  for (const key of ADDRESS_KEYS) {
    const v = (value as Record<string, unknown>)[key];
    if (typeof v === "string") out[key] = v;
  }
  // Card #1998 — a picked address's coordinates survive every coercion (restore, same_as).
  return { ...out, ...addressGeo(value) } as AddressValue;
}

/**
 * An address as a connector envelope carries it — the UK register shape
 * (`address_line_1`, `locality`, `region`, `postal_code`) or this form's own keys — coerced to
 * the answer shape. A plain string lands on line 1. Anything else is an empty address.
 */
export function addressFromEnvelope(raw: unknown): AddressValue {
  if (typeof raw === "string") return raw.trim() ? { line1: raw.trim() } : {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const r = raw as Record<string, unknown>;
  const pick = (...keys: string[]): string | undefined => {
    for (const k of keys) { const v = r[k]; if (typeof v === "string" && v.trim()) return v.trim(); }
    return undefined;
  };
  const out: AddressValue = {};
  const line1 = pick("line1", "address_line_1", "premises");
  const line2 = pick("line2", "address_line_2");
  const town = pick("town", "locality", "post_town");
  const county = pick("county", "region");
  const postcode = pick("postcode", "postal_code");
  if (line1) out.line1 = line1;
  if (line2) out.line2 = line2;
  if (town) out.town = town;
  if (county) out.county = county;
  if (postcode) out.postcode = postcode;
  return out;
}

/**
 * One envelope value as the found-record card shows it: arrays of people become a name list,
 * an address becomes one line, an ISO date reads as a date, scalars read as themselves.
 */
export function envelopeDisplayValue(raw: unknown): string {
  if (raw === undefined || raw === null) return "";
  if (Array.isArray(raw)) {
    return raw.map((item) => {
      if (item && typeof item === "object") {
        const o = item as Record<string, unknown>;
        const name = o.display_name ?? o.name ?? o.title ?? o.formatted;
        return typeof name === "string" ? name : "";
      }
      return String(item);
    }).filter((x) => x.length > 0).join(", ");
  }
  if (typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    if (typeof o.formatted === "string" && o.formatted.trim()) return o.formatted.trim();
    return formatAddress(addressFromEnvelope(raw));
  }
  const text = String(raw);
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return formatDate(text);
  return text;
}

export function isAddressEmpty(a: AddressValue): boolean {
  return ADDRESS_KEYS.every((k) => !(a[k] ?? "").trim());
}

/** "Unit 7, Paintworks, Bath Road, Bristol, BS4 3EH" */
export function formatAddress(a: AddressValue): string {
  return [a.line1, a.line2, a.town, a.county, a.postcode]
    .map((p) => (p ?? "").trim())
    .filter((p) => p.length > 0)
    .join(", ");
}

/** An address is complete when it has a first line, a town and a postcode. */
export function addressError(a: AddressValue, required: boolean): string | undefined {
  if (isAddressEmpty(a)) return required ? "Enter the address, starting with the first line and the postcode." : undefined;
  if (!(a.line1 ?? "").trim()) return "Enter the first line of the address.";
  if (!(a.town ?? "").trim()) return "Enter the town or city.";
  if (!(a.postcode ?? "").trim()) return "Enter the postcode.";
  return undefined;
}

/* ── Display formatting for the review page ────────────────────────────────────────── */

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

/** "2021-04-01" → "1 April 2021"; anything else is returned as typed. */
export function formatDate(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return value;
  const month = MONTHS[Number(m[2]) - 1];
  if (!month) return value;
  return `${Number(m[3])} ${month} ${m[1]}`;
}

export function maskSensitive(value: string): string {
  return "•".repeat(Math.max(6, Math.min(value.trim().length, 12)));
}

/** True when the field's value should be rendered in monospace (reference numbers). */
export function isMonoField(field: Pick<ScalarFieldSpec, "format">): boolean {
  return field.format !== undefined;
}

/** The "(optional)" suffix rule: every field that is not required says so, in the label. */
export function optionalSuffix(field: Pick<FieldSpec, "required">): boolean {
  return field.required !== true;
}

/** Resolve a `same_as` source address from the current answers. */
export function sameAsSource(field: ScalarFieldSpec, values: FormValues, fields: readonly FieldSpec[] | undefined): { label: string; value: AddressValue } | null {
  if (!field.same_as) return null;
  const source = (fields ?? []).find((f) => f.name === field.same_as);
  if (!source) return null;
  const value = asAddress(values[field.same_as]);
  if (isAddressEmpty(value)) return null;
  return { label: source.label ?? humanise(source.name), value };
}

export { resolvePath };
